import { createFileRoute } from "@tanstack/react-router";
import { ServicePage, serviceHead } from "@/components/site/ServicePage";

const SLUG = "energioptimering";
const TITLE = "Energioptimering, sænk dit elforbrug | Elektriker-Elektriker";
const DESC =
  "Energioptimering af bolig og erhverv i Storkøbenhavn. Vi gennemgår dit forbrug, finder besparelserne og leverer en prioriteret tiltagsplan med tilbagebetalingstid.";

export const Route = createFileRoute("/energioptimering")({
  head: () => serviceHead(SLUG, TITLE, DESC),
  component: () => (
    <ServicePage
      slug={SLUG}
      heroVariant="centered"
      accentColor="oklch(0.7 0.16 145)"
      eyebrow="Energioptimering"
      title="Energioptimering der kan ses på regningen"
      intro="Vi gennemgår dit hjem eller din virksomhed systematisk og finder de tiltag, der giver mest besparelse for pengene, fra belysning og styring til varme, ventilation og smart elbilladning."
      longIntro={
        <>
          <h2>Hvad er energioptimering helt konkret?</h2>
          <p>
            Energioptimering er ikke et nyt produkt, det er en metode: vi måler dit faktiske forbrug
            time for time, sammenholder det med bygningen, dit udstyr og dine vaner, og lægger
            derefter en prioriteret plan med konkrete tiltag, hvert tiltag med pris, forventet
            besparelse og tilbagebetalingstid. Du beslutter selv hvor langt du vil gå.
          </p>
          <p>
            For privatkunder ligger den typiske besparelse mellem 15% og 35% på elregningen, og endnu
            mere på varmeregningen hvis du fyrer med el eller olie. For erhverv finder vi ofte tiltag
            der tilbagebetales på under 2 år, særligt indenfor belysning, styring, ventilation og
            tomgang om natten.
          </p>
        </>
      }
      bullets={[
        "Energigennemgang med skriftlig rapport og prioritering",
        "Konkrete tiltag med beregnet tilbagebetalingstid",
        "LED-belysning og bevægelses-/dagslysstyring",
        "Tidsvariabelt forbrug efter elpris og soltimer",
        "Termografering af bolig og tavle",
        "Vi udfører selv tiltagene, eller hjælper dig med at vælge installatør",
      ]}
      features={[
        { title: "Boligejer", body: "Energirapport, prioriteret tiltagsliste, fast tilbud på det vi anbefaler og opfølgning efter 12 måneder." },
        { title: "Ejer-/andelsforening", body: "Fælles besparelser på trapper, kælder, garager og fællesarealer, ofte med flot tilbagebetalingstid." },
        { title: "Erhverv", body: "Drift, belysning, ventilation og styring, vi finder besparelserne uden at gå på kompromis med komforten." },
        { title: "Tidsstyring", body: "Spotpris-styring af varmepumpe, varmtvandsbeholder og ladeboks, du betaler ned, ikke top." },
        { title: "LED-renovering", body: "Udskiftning af lysstofrør og halogen til moderne LED med betalingstid på 1-3 år." },
        { title: "Termografering", body: "Vi finder skjulte kuldebroer, varmegange og fejl i installationen, før de bliver dyre eller farlige." },
      ]}
      process={[
        { title: "Indledende samtale", body: "Vi ringes ved og afklarer hvad du gerne vil opnå og hvor stort dit projekt skal være." },
        { title: "Energigennemgang", body: "2-4 timer på adressen med målinger, foto og noter om alt fra termostat til tavle." },
        { title: "Rapport", body: "Du modtager en skriftlig prioriteret tiltagsplan med pris og tilbagebetalingstid på hvert tiltag." },
        { title: "Udførelse & opfølgning", body: "Vi udfører de tiltag du vælger, og følger op efter 12 måneder for at måle besparelsen." },
      ]}
      faq={[
        { q: "Hvor meget kan vi spare?", a: "Vores private kunder oplever typisk 15-35% besparelse på elregningen efter en grundig optimering. Erhvervskunder ser ofte endnu mere, vi har leveret projekter med 50%+ besparelse på belysning alene." },
        { q: "Hvor lang tid tager en gennemgang?", a: "2-4 timer på adressen plus en rapport. Du har normalt rapporten i hånden indenfor en uge." },
        { q: "Hvad koster en gennemgang?", a: "Vi giver fast tilbud baseret på størrelsen på boligen eller virksomheden, typisk 3.000-12.000 kr. inkl. moms. Beløbet modregnes ofte hvis du bestiller os til selve udførelsen." },
        { q: "Får vi tilskud?", a: "Til en del tiltag (varmepumpe, isolering, vinduer) findes Bygningspuljen og energiselskabernes tilskud. Vi vurderer hvad du kan få i din rapport." },
        { q: "Kan I lave det hele?", a: "Ja, vi er autoriseret elektriker og dækker det el-relaterede selv. Til VVS, isolering og vinduer har vi faste samarbejdspartnere." },
      ]}
      related={[
        { to: "/varmepumper", title: "Varmepumper", body: "Den største enkelt-besparelse for de fleste villaer." },
        { to: "/groen-energi", title: "Grøn energi", body: "Producér selv den strøm du sparer på." },
        { to: "/fejlfinding", title: "Fejlfinding", body: "Skjulte fejl koster også på regningen, vi finder dem." },
      ]}
    />
  ),
});
